export const ROOT_UPPER = 0;

export const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

export const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const normalizeRow = (row) => {
  const childCount = toNumber(pick(row, "childCount", "ChildCount")) ?? 0;
  const idNumberCat = toNumber(pick(row, "idNumberCat", "IdNumberCat"));
  return {
    idNumber: toNumber(pick(row, "idNumber", "IdNumber")),
    idUpper: toNumber(pick(row, "idUpper", "IdUpper")) ?? ROOT_UPPER,
    name: pick(row, "name", "Name") ?? "",
    idNumberCat,
    catName: pick(row, "catName", "CatName") ?? "",
    cntMest: toNumber(pick(row, "cntMest", "CntMest")) ?? 0,
    cntDopMest: toNumber(pick(row, "cntDopMest", "CntDopMest")) ?? 0,
    catIsDel: Number(pick(row, "catIsDel", "CatIsDel")) === 1,
    dateLastChange: pick(row, "dateLastChange", "DateLastChange"),
    square: toNumber(pick(row, "square", "Square")),
    timeIn: pick(row, "timeIn", "TimeIn"),
    timeOut: pick(row, "timeOut", "TimeOut"),
    idLock: toNumber(pick(row, "idLock", "IdLock")),
    idBranch: toNumber(pick(row, "idBranch", "IdBranch")),
    bName: pick(row, "bName", "BName") ?? "",
    online: Number(pick(row, "online", "Online")) === 1,
    dateDaysMax: pick(row, "dateDaysMax", "DateDaysMax"),
    childCount,
    /** Группа — папка: есть потомки или нет категории (ID_NUMBER_CAT = 0). */
    isGroup: childCount > 0 || !(idNumberCat > 0)
  };
};

/** Корни: ID_UPPER = 0 / null, либо родитель отсутствует в выборке. */
const resolveParentId = (idUpper, idSet) => {
  if (idUpper == null || idUpper === ROOT_UPPER) return ROOT_UPPER;
  if (!idSet.has(idUpper)) return ROOT_UPPER;
  return idUpper;
};

/** Дерево только групп (папки: потомки или без категории). */
export const buildGroupTree = (rows) => {
  const list = (Array.isArray(rows) ? rows : [])
    .map(normalizeRow)
    .filter((r) => r.idNumber != null && r.isGroup);
  const idSet = new Set(list.map((r) => r.idNumber));
  const byParent = new Map();

  list.forEach((row) => {
    const parent = resolveParentId(row.idUpper, idSet);
    let bucket = byParent.get(parent);
    if (!bucket) {
      bucket = [];
      byParent.set(parent, bucket);
    }
    bucket.push(row);
  });

  const build = (parentId, stack = new Set()) =>
    (byParent.get(parentId) || []).map((row) => {
      if (stack.has(row.idNumber)) {
        return {
          key: String(row.idNumber),
          idNumber: row.idNumber,
          title: row.name,
          name: row.name,
          childCount: row.childCount,
          isLeaf: true
        };
      }
      stack.add(row.idNumber);
      const children = build(row.idNumber, stack);
      stack.delete(row.idNumber);
      return {
        key: String(row.idNumber),
        idNumber: row.idNumber,
        title: row.name,
        name: row.name,
        childCount: row.childCount,
        isLeaf: !children.length,
        ...(children.length ? { children } : {})
      };
    });

  return build(ROOT_UPPER);
};

/** Полное дерево (для parentOptions и поиска). */
export const buildNumberTree = (rows) => {
  const list = (Array.isArray(rows) ? rows : []).map(normalizeRow).filter((r) => r.idNumber != null);
  const idSet = new Set(list.map((r) => r.idNumber));
  const byParent = new Map();

  list.forEach((row) => {
    const parent = resolveParentId(row.idUpper, idSet);
    byParent.set(parent, [...(byParent.get(parent) || []), row]);
  });

  const build = (parentId) =>
    (byParent.get(parentId) || []).map((row) => {
      const children = build(row.idNumber);
      return {
        key: String(row.idNumber),
        ...row,
        ...(children.length ? { children } : {})
      };
    });

  return build(ROOT_UPPER);
};

export const flattenParentOptions = (nodes, depth = 0, acc = []) => {
  (nodes || []).forEach((node) => {
    const pad = depth > 0 ? `${"\u00A0".repeat(depth * 3)}` : "";
    acc.push({ value: node.idNumber, label: `${pad}${node.name || `№${node.idNumber}`}` });
    if (node.children?.length) flattenParentOptions(node.children, depth + 1, acc);
  });
  return acc;
};

export const findInTree = (nodes, idNumber) => {
  for (const node of nodes || []) {
    if (node.idNumber === idNumber) return node;
    const nested = findInTree(node.children, idNumber);
    if (nested) return nested;
  }
  return null;
};

export const collectGroupKeys = (nodes, acc = new Set()) => {
  (nodes || []).forEach((node) => {
    if (node?.key != null && node.key !== "root") acc.add(String(node.key));
    if (node?.children?.length) collectGroupKeys(node.children, acc);
  });
  return acc;
};

export const collectExpandableKeys = (nodes, acc = []) => {
  (nodes || []).forEach((node) => {
    if (node.children?.length) {
      acc.push(node.key);
      collectExpandableKeys(node.children, acc);
    }
  });
  return acc;
};

export const formatTime = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
};

const TABLE_FONT = `13px Manrope, "Segoe UI", Tahoma, sans-serif`;
const TABLE_HEADER_FONT = `700 12px Manrope, "Segoe UI", Tahoma, sans-serif`;

let measureCtx;

const getMeasureCtx = () => {
  if (typeof document === "undefined") return null;
  if (!measureCtx) {
    measureCtx = document.createElement("canvas").getContext("2d");
  }
  return measureCtx;
};

/** Ширина колонки по заголовку и ячейкам, не меньше min. */
export const measureContentColWidth = (header, values, { min = 100, pad = 20, bodyFont } = {}) => {
  const ctx = getMeasureCtx();
  if (!ctx) return min;
  ctx.font = TABLE_HEADER_FONT;
  let max = ctx.measureText(String(header || "")).width;
  ctx.font = bodyFont || TABLE_FONT;
  for (const value of values) {
    const w = ctx.measureText(String(value ?? "")).width;
    if (w > max) max = w;
  }
  return Math.max(min, Math.ceil(max + pad));
};

export const formatDate = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ru-RU");
};

export const formatNumber = (value) => {
  const n = toNumber(value);
  if (n == null) return "";
  return String(n);
};

export const formatSquare = (value) => {
  const n = toNumber(value);
  if (n == null || n === 0) return "";
  return `${n} м²`;
};

/** Delphi CNT_MEST / CNT_DOP_MEST → «2» или «2+1». */
export const formatCapacity = (cntMest, cntDopMest) => {
  const mest = toNumber(cntMest) ?? 0;
  const dop = toNumber(cntDopMest) ?? 0;
  if (mest <= 0 && dop <= 0) return "";
  if (dop <= 0) return String(mest);
  return `${mest}+${dop}`;
};

export const roomStatus = (row) => {
  if (row?.catIsDel) return "inactive";
  if (row?.online) return "online";
  return "active";
};

export const isUnderGroup = (row, groupId, byId) => {
  if (groupId == null || groupId === ROOT_UPPER) return true;
  let id = row?.idUpper;
  for (let i = 0; i < 64 && id > 0; i += 1) {
    if (id === groupId) return true;
    const parent = byId.get(id);
    if (!parent) return false;
    id = parent.idUpper;
  }
  return false;
};

export const countDescendantLeaves = (groupId, rows) => {
  const list = Array.isArray(rows) ? rows : [];
  if (groupId === ROOT_UPPER) {
    return list.reduce((n, r) => n + (r.isGroup ? 0 : 1), 0);
  }
  const byId = new Map(list.map((r) => [r.idNumber, r]));
  return list.reduce((n, r) => n + (!r.isGroup && isUnderGroup(r, groupId, byId) ? 1 : 0), 0);
};

export const attachLeafCounts = (nodes, rows) =>
  (nodes || []).map((node) => ({
    ...node,
    leafCount: countDescendantLeaves(node.idNumber, rows),
    ...(node.children?.length ? { children: attachLeafCounts(node.children, rows) } : {})
  }));
